/**
 * TARA Workflow v2 — step catalog.
 *
 * Single source of truth for the 9-step list (matches the legacy
 * STEP_TO_AGENT_MAP in ProjectWorkspace.jsx but lives next to the v2
 * code so the legacy substrate stays untouched). The `agent` field
 * names the backend agent that produces this step's result; the
 * `helperText` is the one-liner that StepShell renders under the step
 * title.
 *
 * Step 10 (Residual TARA) is intentionally absent — it lives in its
 * own ProjectWorkspace tab and isn't part of the v2 workflow tab.
 */

export interface StepCatalogEntry {
  id: number;
  title: string;
  clause: string | null;
  /** Backend agent name. `null` for agentless steps (Step 0). */
  agent: string | null;
  helperText: string;
}

export const STEP_CATALOG: StepCatalogEntry[] = [
  {
    // Step 0 — upload-only entry point. No backend agent; status is
    // driven purely by the frontend upload flow (empty → generating →
    // approved | failed). HYDRATE_FROM_BACKEND skips steps with
    // agent === null. The ActionPanel is hidden when Step 0 is
    // selected (no review chat, no Approve CTA).
    id: 0,
    title: 'Item Definition Setup',
    clause: null,
    agent: null,
    helperText:
      'Set up your Item Definition by choosing one of three input methods.',
  },
  {
    id: 1,
    title: 'Item Definition',
    clause: 'ISO 21434 §9.3',
    agent: 'analysis_agent',
    helperText:
      'Define the system under analysis. Outputs become the structured input for asset identification.',
  },
  {
    id: 2,
    title: 'Asset Identification',
    clause: 'ISO 21434 §9.3',
    agent: 'asset_identification_agent',
    helperText:
      'Enumerate the assets in scope with CIA properties and security relevance. Each asset becomes a target node in subsequent damage and threat analysis.',
  },
  {
    id: 3,
    title: 'Damage Scenarios',
    clause: 'ISO 21434 §15.3',
    agent: 'damage_scenario_agent',
    helperText:
      'Describe the impacts that compromised assets would cause across Safety, Financial, Operational, and Privacy dimensions.',
  },
  {
    id: 4,
    title: 'Threat Identification',
    clause: 'ISO 21434 §15.4',
    agent: 'threat_analysis_agent',
    helperText:
      'Enumerate STRIDE-categorised threats against each asset, with likelihood and UN R155 mapping.',
  },
  {
    id: 5,
    title: 'Attack Path Feasibility',
    clause: 'ISO 21434 §15.5',
    agent: 'attack_path_feasibility_agent',
    helperText:
      'Trace plausible attack paths from threat actors to assets; rate feasibility by elapsed time, expertise, knowledge, window of opportunity, and equipment.',
  },
  {
    id: 6,
    title: 'Risk Assessment',
    clause: 'ISO 21434 §15.6',
    agent: 'risk_assessment_agent',
    helperText:
      'Combine damage severity and attack feasibility into a risk score per scenario; choose the treatment (reduce / accept / transfer / avoid).',
  },
  {
    id: 7,
    title: 'Cybersecurity Goals',
    clause: 'ISO 21434 §9.4',
    agent: 'cybersecurity_goals_agent',
    helperText:
      'Define the high-level cybersecurity goals and claims that the design must satisfy, tied to risks needing reduction.',
  },
  {
    id: 8,
    title: 'Cybersecurity Concept',
    clause: 'ISO 21434 §9.5',
    agent: 'cybersecurity_concept_agent',
    helperText:
      'Specify the controls and requirements that realise each goal; map them back to the risks they mitigate.',
  },
];

/* Step 9 (Final Report) was previously the last entry in this
 * catalog. Removed — the per-project Reports tab already exposes the
 * same export surface (XLSX / CSV / JSON / OpenXSAM / ISO 21434 Work
 * Products / Custom Report Builder) via `Results/ReportsPage`, so an
 * in-workflow Step 9 tab was redundant. After approving Step 8 the
 * user lands on `workflowCompleted` and can switch to the Reports
 * tab via the project's tab nav. */

/** Look up a step entry by id. Returns undefined if not found. */
export const findStep = (id: number): StepCatalogEntry | undefined =>
  STEP_CATALOG.find((s) => s.id === id);

/** Map step id → agent name. */
export const stepToAgent = (id: number): string | null | undefined =>
  findStep(id)?.agent;

/** Map agent name → step id. */
export const agentToStep = (agentName: string): number | undefined =>
  STEP_CATALOG.find((s) => s.agent === agentName)?.id;

/**
 * Map step id → the NEXT step's agent name (the agent the backend
 * runs when the user approves this step). Returns null for the final
 * step. Used by `resumeWorkflow({ agentName })` — the backend's
 * contract is "agent_name is the resume TARGET, not the agent being
 * approved" (the previous agent is auto-marked approved).
 */
export const nextAgent = (id: number): string | null =>
  stepToAgent(id + 1) ?? null;
